import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, Download } from 'lucide-react';
import { apiGet } from '../lib/api';
import { formatNTD, formatDate } from '@yiweiwu/shared';
import type { OrderSummary } from '../lib/types';
import { StatusBadge } from '../components/StatusBadge';
import { FilterBar } from '../components/FilterBar';
import { DataTable, type Column } from '../components/DataTable';

const STATUS_OPTIONS = [
  { value: 'pending_payment', label: '待付款' },
  { value: 'paid', label: '已付款' },
  { value: 'preparing', label: '備貨中' },
  { value: 'shipped', label: '已出貨' },
  { value: 'arrived', label: '已送達' },
  { value: 'completed', label: '已完成' },
  { value: 'cancelled', label: '已取消' },
  { value: 'returned', label: '已退回' },
];

interface PaginatedOrders {
  items: OrderSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export default function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PaginatedOrders | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: Record<string, string> = { page: String(page), pageSize: '20' };
      if (status) params.status = status;
      if (search) params.search = search;

      const result = await apiGet<PaginatedOrders>('/orders', params);
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '載入訂單失敗');
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const handleClear = () => {
    setSearchParams({});
  };

  const handleExportCSV = () => {
    const params: Record<string, string> = {};
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    const qs = new URLSearchParams(params).toString();
    window.open(`/admin-api/exports/orders.csv${qs ? `?${qs}` : ''}`, '_blank');
  };

  const columns: Column<OrderSummary>[] = [
    {
      key: 'orderNumber',
      header: '訂單編號',
      render: (row) => (
        <Link
          to={`/admin/orders/${row.id}`}
          className="font-mono text-sm text-brand-gold hover:underline"
        >
          {row.orderNumber}
        </Link>
      ),
    },
    {
      key: 'createdAt',
      header: '日期',
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'customerName',
      header: '客戶',
      render: (row) => (
        <span className="text-gray-900">{row.customerName}</span>
      ),
    },
    {
      key: 'items',
      header: '品項',
      render: (row) => (
        <span className="text-gray-600">
          {row.items.map((it) => `${it.name} x${it.quantity}`).join(', ')}
        </span>
      ),
    },
    {
      key: 'total',
      header: '金額',
      render: (row) => (
        <span className="font-medium text-gray-900">
          {formatNTD(row.total)}
        </span>
      ),
    },
    {
      key: 'status',
      header: '狀態',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'paymentStatus',
      header: '付款',
      render: (row) => <StatusBadge status={row.paymentStatus} type="payment" />,
    },
    {
      key: 'actions',
      header: '操作',
      render: (row) => (
        <Link
          to={`/admin/orders/${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-brand-gold hover:bg-brand-gold/10 transition-colors"
        >
          <Eye className="h-3.5 w-3.5" />
          查看
        </Link>
      ),
    },
  ];

  return (
    <div>
      {/* 頁首 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-brand-cocoa font-heading">
            訂單管理
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {data ? `共 ${data.total} 筆訂單` : '載入中...'}
          </p>
        </div>
        <button
          onClick={handleExportCSV}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-gold text-white text-sm font-medium hover:bg-brand-gold/90 transition-colors"
        >
          <Download className="h-4 w-4" />
          匯出 CSV
        </button>
      </div>

      {/* 篩選列 */}
      <FilterBar
        searchPlaceholder="搜尋訂單編號..."
        searchValue={search}
        onSearchChange={(v) => updateParam('search', v)}
        statusValue={status}
        onStatusChange={(v) => updateParam('status', v)}
        statusOptions={STATUS_OPTIONS}
        dateFromValue={searchParams.get('dateFrom') ?? ''}
        onDateFromChange={(v) => updateParam('dateFrom', v)}
        dateToValue={searchParams.get('dateTo') ?? ''}
        onDateToChange={(v) => updateParam('dateTo', v)}
        onClear={handleClear}
      />

      {/* 錯誤訊息 */}
      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
          <button
            onClick={fetchOrders}
            className="ml-3 underline hover:text-red-800"
          >
            重試
          </button>
        </div>
      )}

      {/* 資料表 */}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(r) => r.id}
        loading={loading}
        emptyMessage="尚無訂單資料"
      />

      {/* 分頁 */}
      {data && data.total > data.pageSize && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            第 {data.page} / {Math.ceil(data.total / data.pageSize)} 頁
          </span>
          <div className="flex gap-2">
            <button
              disabled={data.page <= 1}
              onClick={() => updateParam('page', String(data.page - 1))}
              className="px-3 py-1.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              上一頁
            </button>
            <button
              disabled={data.page >= Math.ceil(data.total / data.pageSize)}
              onClick={() => updateParam('page', String(data.page + 1))}
              className="px-3 py-1.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一頁
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
